import { useState } from 'react';
import { useParams } from 'react-router';
import { editableFields, payFromAccount, type ExpenseFields } from '@tahi/core';
import { editExpense } from '../../../data/commands/spending';
import { useMethodOptions } from '../../../data/methodOptions';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import { showToast } from '../../../ui';
import { SpendPickers } from '../../budget/components/SpendPickers';
import { copy as budgetCopy } from '../../budget/copy';
import { useAccounts, useBudgetSettings, useFunds } from '../../budget/hooks';
import { QuickAddForm } from '../QuickAddForm';
import { copy, errorText } from '../copy';
import { sourceLabel } from '../format';
import { useCategories, useEntry, useGoBack, useNoteHistory, useToday } from '../hooks';
import { SCREEN } from '../styles';

const FIELDS = ['amountMinor', 'categoryId', 'note', 'date', 'method'] as const;

export function EditEntryRoute() {
  const env = useCommandEnv();
  const { id = '' } = useParams();
  const goBack = useGoBack(`/m/spending/entries/${id}`);
  const methods = useMethodOptions();
  const today = useToday();
  const entry = useEntry(id);
  const categories = useCategories() ?? [];
  const noteHistory = useNoteHistory() ?? [];
  const usable = useUsable();
  const funds = useFunds() ?? [];
  const [error, setError] = useState<string | null>(null);
  // Budget (v0.2): move an expense to another fund (or none) while Budget is usable.
  const [fundId, setFundId] = useState<string | null | undefined>(undefined);
  // v0.5: change which account it was paid from.
  const [accountId, setAccountId] = useState<string | null | undefined>(undefined);
  const settings = useBudgetSettings();
  const accounts = useAccounts() ?? [];

  if (entry === undefined) return null;
  if (entry === null || entry.deletedAt) {
    return (
      <main className={`${SCREEN} md:max-w-2xl`}>
        <p className="text-muted">{copy.detail.notFound}</p>
      </main>
    );
  }

  const editable = editableFields(entry);
  const locked = FIELDS.filter((f) => !editable.includes(f));

  const budget = usable('budget') && entry.kind === 'expense';
  const showFunds = budget && !!settings?.useFunds && funds.length > 0;
  const pickedAccount = accountId === undefined ? entry.accountId : accountId;
  const pickedFund = fundId === undefined ? entry.fundId : fundId;
  // The entry's fund was deleted: no chip matches, so say so and let the user pick another or none.
  const fundGone = pickedFund !== null && !funds.some((f) => f.id === pickedFund);
  // v0.7.6: the card can change here unless another module keeps the method (Payments rows).
  const cards = budget && editable.includes('method');
  const isCard = (m: string | null | undefined) => !!m && m.startsWith('card:');

  async function onSubmit(fields: ExpenseFields) {
    // Moving a card purchase back to no card with no account picked: it comes out of the default
    // account (the limit's, else "Pay from"), like a new expense, so it isn't from nowhere.
    let account = pickedAccount;
    if (budget && settings && isCard(entry?.method) && !isCard(fields.method) && account === null) {
      const fund = funds.find((f) => f.id === pickedFund) ?? null;
      account = payFromAccount(null, fund, settings, true, accounts);
    }
    const r = await editExpense(env, id, {
      ...fields,
      ...(showFunds ? { fundId: pickedFund } : {}),
      ...(budget ? { accountId: account } : {}),
    });
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    if (Object.keys(r.undo).length > 0) {
      showToast({
        message: copy.saved,
        action: { label: copy.undo, onPress: () => void editExpense(env, id, r.undo) },
      });
    }
    goBack();
  }

  return (
    <QuickAddForm
      methods={methods}
      mode="edit"
      title={copy.editTitle}
      onClose={goBack}
      today={today}
      categories={categories}
      noteHistory={noteHistory}
      initial={{
        amountMinor: entry.amountMinor,
        categoryId: entry.categoryId,
        note: entry.note,
        date: entry.date,
        method: entry.method,
      }}
      cardsInFrom={cards}
      extra={
        budget
          ? ({ date, method, setMethod }) => (
              <SpendPickers
                withFund={showFunds}
                fundId={fundGone ? null : pickedFund}
                onFund={setFundId}
                fundHintText={fundGone ? budgetCopy.fundRemoved : budgetCopy.fundHintMoveNone}
                accountId={pickedAccount}
                onAccount={setAccountId}
                {...(cards ? { card: { method, onMethod: setMethod, date } } : {})}
              />
            )
          : undefined
      }
      lockedFields={locked}
      lockedReason={
        locked.length > 0 ? copy.detail.fromOtherModule(sourceLabel(entry.sourceModule)) : undefined
      }
      error={error}
      onSubmit={onSubmit}
    />
  );
}
